import { Section } from "@redotech/redo-model/email-builder";
import { ShopifyGraphQLProduct } from "@redotech/redo-model/product";
import { UpsellProductSource } from "@redotech/redo-model/team";
import { TrackableLineItem } from "@redotech/redo-model/trackable";
import { CurrencyCode } from "@redotech/shopify-client/storefront.graphql";
import { ProductInfo } from "@redotech/shopify-storefront";
import { stripHtml } from "@redotech/util/html";

export type StaticRecommendedProduct = Pick<
  TrackableLineItem,
  "title" | "variantTitle" | "price" | "image" | "quantity" | "handle"
> & { pdpUrl: string; alreadyInCart: boolean; id: string };

type RecommendedProduct = Omit<ProductInfo, "variants"> & {
  variants: Omit<ProductInfo["variants"], "nodes"> & {
    nodes: Array<
      Omit<
        ProductInfo["variants"]["nodes"][0],
        "requiresShipping" | "weight" | "weightUnit" | "currentlyNotInStock"
      >
    >;
  };
  altImage: { url?: string; altText?: string };
  price: string;
};

export function productInfoToRecommendedProduct(
  productInfo: ShopifyGraphQLProduct,
): RecommendedProduct {
  return {
    id: productInfo.id,
    handle: productInfo.handle,
    title: productInfo.title,
    description: productInfo.description,
    vendor: productInfo.vendor ?? "",
    onlineStoreUrl: productInfo.onlineStoreUrl,
    totalInventory: productInfo.variants.nodes[0].inventoryQuantity,
    productType: productInfo.productType,
    availableForSale: productInfo.variants.nodes[0].availableForSale,
    tags: productInfo.tags,
    publishedAt: productInfo.publishedAt,
    altImage: { url: productInfo.images.nodes[0].url ?? "", altText: "" },
    price: productInfo.variants.nodes[0].price,
    collections: { nodes: [] },
    options: [],
    priceRange: {
      minVariantPrice: {
        amount: productInfo.priceRange.minVariantPrice.amount,
        currencyCode: productInfo.priceRange.minVariantPrice
          .currencyCode as CurrencyCode,
      },
      maxVariantPrice: {
        amount: productInfo.priceRange.maxVariantPrice.amount,
        currencyCode: productInfo.priceRange.maxVariantPrice
          .currencyCode as CurrencyCode,
      },
    },
    variants: {
      nodes: productInfo.variants.nodes.map((v) => ({
        ...v,
        price: { amount: v.price, currencyCode: CurrencyCode.Usd },
      })),
    },
    images: {
      nodes: productInfo.images.nodes.map((i) => ({
        url: i.url ?? "",
        altText: "",
      })),
    },
  };
}

export function recommendedProductToStaticRecommendedProduct(
  sectionPropsCart: Section.InteractiveCart,
  product: RecommendedProduct | null,
  { alreadyInCart }: { alreadyInCart: boolean },
): StaticRecommendedProduct | null {
  if (!product) return null;
  const variantId = sectionPropsCart.manuallySelectedProducts?.find(
    (msp) => msp?.productId === product.id,
  )?.variantId;
  const variantIndex =
    sectionPropsCart.source === UpsellProductSource.MANUAL
      ? product.variants.nodes.findIndex((variant) => variant.id === variantId)
      : 0;
  const variant = product.variants.nodes[variantIndex];
  const imageSrc =
    variant.image?.url || product.featuredImage?.url || product.altImage.url;
  return {
    id: product.id,
    price: Number(variant?.price?.amount ?? product.price),
    image: { src: imageSrc },
    title: stripHtml(product.title),
    variantTitle: stripHtml(variant.title),
    pdpUrl: product.onlineStoreUrl,
    handle: product.handle,
    alreadyInCart,
    quantity: 1,
  };
}
